import {
  defineRole,
  defineRoleGroup,
} from '@components/features/Restricted/utils';

const viewer = defineRole('viewer', {
  label: 'viewer',
  description: 'Read-only access to resources',
  context: 'team',
  match: { roleId: 3 },
});

const developer = defineRole('developer', {
  label: 'developer',
  description: 'Can create and manage resources',
  context: 'team',
  match: { roleId: 2 },
});

const admin = defineRole('admin', {
  label: 'admin',
  description: 'Can manage team settings',
  context: 'team',
  match: { roleId: 1 },
});

const user = defineRole('user', {
  label: 'user',
  context: 'global',
  match: { roleName: 'user' },
});

const support = defineRole('support', {
  label: 'support user',
  context: 'global',
  match: { roleName: 'support' },
});

const superuser = defineRole('superuser', {
  label: 'superuser',
  context: 'global',
  match: { roleName: 'superuser' },
});

const roleRegistry = {
  // Team context roles
  [viewer.key]: viewer,
  [developer.key]: developer,
  [admin.key]: admin,

  // Global context roles
  [user.key]: user,
  [support.key]: support,
  [superuser.key]: superuser,
};

type RoleKey = keyof typeof roleRegistry;
type Role = (typeof roleRegistry)[RoleKey];
const roleKeys = Object.keys(roleRegistry) as RoleKey[];

const internal = defineRoleGroup('internal', [support, superuser]);

const devOrAdmin = defineRoleGroup('devOrAdmin', [developer, admin]);

const roleGroupRegistry = {
  [internal.key]: internal,
  [devOrAdmin.key]: devOrAdmin,
};

type RoleGroupKey = keyof typeof roleGroupRegistry;
type RoleGroup = (typeof roleGroupRegistry)[RoleGroupKey];
const roleGroupKeys = Object.keys(roleGroupRegistry) as RoleGroupKey[];

export const roleRegistryKeys = [...roleKeys, ...roleGroupKeys];
export type RoleRegistryKey = RoleKey | RoleGroupKey;
export type RoleRegistry<TKey extends RoleRegistryKey> = TKey extends RoleKey
  ? (typeof roleRegistry)[TKey]
  : TKey extends RoleGroupKey
    ? (typeof roleGroupRegistry)[TKey]
    : never;

export function getRole<TKey extends RoleRegistryKey>(
  key: TKey
): RoleRegistry<TKey> {
  if (key in roleRegistry) {
    return roleRegistry[key as RoleKey] as RoleRegistry<TKey>;
  }
  if (key in roleGroupRegistry) {
    return roleGroupRegistry[key as RoleGroupKey] as RoleRegistry<TKey>;
  }
  throw new Error(`Role or role group with key "${key}" not found`);
}

export { type Role as RegisteredRole, type RoleGroup as RegisteredRoleGroup };
